// Client API cookie-aware (Option A — Sanctum SPA).
//  - `credentials: 'include'` sur toutes les requêtes (envoie/reçoit le cookie de session).
//  - Bootstrap CSRF avant toute mutation : GET /sanctum/csrf-cookie puis header X-XSRF-TOKEN.
//  - Parse l'enveloppe {status,data,message,code,errors} → renvoie `data` ou throw ApiError.
// Voir docs/frontend-web/05-reference-api.md.

import { ApiError, type ApiEnvelope } from '@/types/api';

const BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? '';

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

interface RequestOptions {
  method?: Method;
  body?: unknown;
  signal?: AbortSignal;
}

// Lit un cookie non-httpOnly (XSRF-TOKEN est lisible par le JS, contrairement à la session).
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

let csrfReady = false;

// Récupère le cookie XSRF-TOKEN une fois par session client (avant la 1re mutation).
async function ensureCsrfCookie(): Promise<void> {
  if (csrfReady || readCookie('XSRF-TOKEN')) {
    csrfReady = true;
    return;
  }
  await fetch(`${BASE_URL}/sanctum/csrf-cookie`, { credentials: 'include' });
  csrfReady = true;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  // Les mutations exigent le token CSRF (côté web stateful).
  if (method !== 'GET') {
    await ensureCsrfCookie();
    const xsrf = readCookie('XSRF-TOKEN');
    if (xsrf) headers['X-XSRF-TOKEN'] = xsrf;
  }

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      credentials: 'include',
      signal: options.signal,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });
  } catch {
    throw new ApiError('NETWORK_ERROR', 'Impossible de contacter le serveur.', 0);
  }

  let payload: ApiEnvelope<T> | null = null;
  try {
    payload = (await res.json()) as ApiEnvelope<T>;
  } catch {
    // Réponse non-JSON (ex. 419/500 HTML) → erreur générique portant le statut HTTP.
    throw new ApiError('UNEXPECTED_RESPONSE', `Réponse inattendue (HTTP ${res.status}).`, res.status);
  }

  if (payload.status === 'OK') {
    return payload.data;
  }

  throw new ApiError(payload.code, payload.message, res.status, payload.errors ?? {});
}
